/**
 * UpdateSocialForm
 *
 * Form component for updating user's social links (Twitter, LinkedIn, Instagram).
 * - Initializes state from userDetails (user's current social URLs).
 * - Normalizes URLs before submitting to backend.
 * - Submits updated social links via useSocialUpdate hook.
 * - Displays status messages for loading, errors, and success.
 */

import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField } from "@mui/material";
import { useSocialUpdate } from "../../hooks/useSocialUpdate";
import { useProfileUpdateContext } from "../../contexts/ProfileUpdateContext";
import { formContainer } from "./styles";

function normalizeUrl(input: string): string {
  const v = (input || "").trim();
  if (!v) return "";
  if (v.startsWith("@")) return `https://twitter.com/${v.slice(1)}`;
  const lower = v.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://")) return v;
  return `https://${v}`;
}

export type UserSocialData = {
  twitter?: string;
  linkedIn?: string;
  instagram?: string;
};

export default function UpdateSocialForm(userDetails: UserSocialData) {
  const { status, clearStatus, send } = useSocialUpdate();
  const { profileData, setProfileData } = useProfileUpdateContext();

  const [twitter, setTwitter] = useState("");
  const [linkedIn, setLinkedIn] = useState("");
  const [instagram, setInstagram] = useState("");

  useEffect(() => {
    clearStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userDetails) return;
    setTwitter(userDetails.twitter || "");
    setLinkedIn(userDetails.linkedIn || "");
    setInstagram(userDetails.instagram || "");
  }, [userDetails]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status.loading) return;
    clearStatus();
    try {
      const updated = {
        ...profileData,
        twitter: normalizeUrl(twitter),
        linkedIn: normalizeUrl(linkedIn),
        instagram: normalizeUrl(instagram),
        _id: profileData?._id ?? "",
        name: profileData?.name ?? "",
        last_name: profileData?.last_name ?? "",
        email: profileData?.email ?? "",
        description: profileData?.description ?? "",
        location: profileData?.location ?? "",
        city: profileData?.city ?? "",
        institute: profileData?.institute ?? "",
        image: profileData?.image ?? "",
        disciplines: profileData?.disciplines ?? [],
        industries: profileData?.industries ?? [],
        keywords: profileData?.keywords ?? [],
      };
      await send({
        twitter: normalizeUrl(twitter),
        linkedIn: normalizeUrl(linkedIn),
        instagram: normalizeUrl(instagram),
      });
      setProfileData(updated);
    } catch (err) {
      console.error("Social update error:", err);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={formContainer}>
      {status.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.error}
        </Alert>
      )}
      {status.success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {status.success}
        </Alert>
      )}
      {status.loading && <CircularProgress sx={{ mb: 2 }} />}

      <TextField
        label="Twitter URL"
        value={twitter}
        onChange={(e) => setTwitter(e.target.value)}
        fullWidth
        margin="normal"
      />
      <TextField
        label="LinkedIn URL"
        value={linkedIn}
        onChange={(e) => setLinkedIn(e.target.value)}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Instagram URL"
        value={instagram}
        onChange={(e) => setInstagram(e.target.value)}
        fullWidth
        margin="normal"
      />

      <Button
        type="submit"
        variant="contained"
        sx={{ mt: 2 }}
        disabled={status.loading}
      >
        Update
      </Button>
    </Box>
  );
}
